import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Users, CalendarDays, DollarSign, LogOut, Plus, Trash2, Pencil,
  MoreVertical, LayoutDashboard, Download, Save,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="glass rounded-xl p-6 flex items-center gap-4" data-testid={`stat-${label}`}>
    <div className="w-12 h-12 rounded-lg bg-purple-600/20 border border-purple-500/30 flex items-center justify-center text-purple-400">
      <Icon size={22} />
    </div>
    <div>
      <div className="font-display text-3xl">{value}</div>
      <div className="text-xs uppercase tracking-wider text-white/45">{label}</div>
    </div>
  </div>
);

const emptyEvent = { title: "", description: "", location: "", rsvp_url: "" };
const inputCls = "w-full bg-transparent border border-white/20 rounded-sm px-3 py-2.5 text-white text-sm placeholder-white/35 focus:outline-none focus:border-purple-500 transition-colors";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ volunteers: 0, upcoming_events: 0, donations_count: 0, donations_total: 0 });
  const [events, setEvents] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvent);
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("18:00");
  const { refresh: refreshContent } = useContent();
  const [cForm, setCForm] = useState(null);
  const [bioLang, setBioLang] = useState("en");
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, e, v, d, c] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/events"),
        api.get("/admin/volunteers"), api.get("/admin/donations"),
        api.get("/admin/content"),
      ]);
      setStats(s.data); setEvents(e.data); setVolunteers(v.data); setDonations(d.data);
      try { const rs = await api.get("/admin/rsvps"); setRsvps(rs.data); } catch { /* ignore */ }
      const c0 = c.data || {};
      setCForm({
        name: c0.name || "",
        portrait: c0.portrait || "",
        stat1: c0.stat1 || "", stat2: c0.stat2 || "", stat3: c0.stat3 || "",
        donation_goal: c0.donation_goal || 25000,
        bios: c0.bios || {},
      });
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => { if (user && user.role) loadAll(); }, [user, loadAll]);

  const openNew = () => { setEditing(null); setForm(emptyEvent); setDate(new Date()); setTime("18:00"); setDialogOpen(true); };
  const openEdit = (ev) => {
    setEditing(ev); setForm({ title: ev.title, description: ev.description, location: ev.location, rsvp_url: ev.rsvp_url || "" });
    const d = new Date(ev.starts_at); setDate(d); setTime(format(d, "HH:mm")); setDialogOpen(true);
  };

  const saveEvent = async () => {
    if (!form.title || !date) { toast.error("Title and date are required"); return; }
    const [h, m] = time.split(":");
    const dt = new Date(date); dt.setHours(parseInt(h || 0), parseInt(m || 0), 0, 0);
    const payload = { ...form, starts_at: dt.toISOString() };
    try {
      if (editing) await api.put(`/admin/events/${editing.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(editing ? "Event updated" : "Event created");
      setDialogOpen(false); loadAll();
    } catch (err) { toast.error("Could not save event"); }
  };

  const removeEvent = async (id) => {
    try { await api.delete(`/admin/events/${id}`); toast.success("Event deleted"); loadAll(); }
    catch { toast.error("Could not delete"); }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const setBio = (field) => (e) => {
    const val = e.target.value;
    setCForm((prev) => ({
      ...prev,
      bios: { ...prev.bios, [bioLang]: { ...(prev.bios[bioLang] || {}), [field]: val } },
    }));
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/admin/content", cForm);
      await refreshContent();
      toast.success("Candidate profile updated");
    } catch { toast.error("Could not save profile"); }
    finally { setSavingContent(false); }
  };

  const exportDonations = async () => {
    try {
      const res = await api.get("/admin/donations/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `donations_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Donations exported");
    } catch { toast.error("Export failed"); }
  };

  if (!user || !user.role) return <div className="min-h-screen bg-[#050505]" />;

  return (
    <div className="min-h-screen bg-[#050505] text-white grain" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-black/70 backdrop-blur-xl z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="text-purple-400" size={20} />
            <span className="font-display uppercase text-lg tracking-wide">Campaign Console</span>
          </div>
          <button data-testid="admin-logout" onClick={doLogout} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Volunteers" value={stats.volunteers} />
          <StatCard icon={CalendarDays} label="Upcoming Events" value={stats.upcoming_events} />
          <StatCard icon={DollarSign} label="Donations" value={stats.donations_count} />
          <StatCard icon={DollarSign} label="Total Raised" value={`$${(stats.donations_total / 100).toLocaleString()}`} />
        </div>

        <Tabs defaultValue="events">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="volunteers" data-testid="tab-volunteers">Volunteers</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
            <TabsTrigger value="rsvps" data-testid="tab-rsvps">RSVPs</TabsTrigger>
            <TabsTrigger value="candidate" data-testid="tab-candidate">Candidate</TabsTrigger>
          </TabsList>

          {/* EVENTS */}
          <TabsContent value="events" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display uppercase text-2xl">Events</h2>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button data-testid="new-event-btn" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-purple-600 hover:bg-purple-500 text-sm font-semibold transition-colors">
                    <Plus size={16} /> New Event
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0c0c0c] border-white/10 text-white">
                  <DialogHeader><DialogTitle className="font-display uppercase">{editing ? "Edit Event" : "New Event"}</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <input data-testid="event-title" className={inputCls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <textarea data-testid="event-desc" rows={3} className={inputCls} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    <input data-testid="event-location" className={inputCls} placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="date"
                        data-testid="event-date"
                        className={inputCls}
                        value={date ? format(date, "yyyy-MM-dd") : ""}
                        onChange={(e) => {
                          const [y, m, d] = e.target.value.split("-").map(Number);
                          if (y && m && d) setDate(new Date(y, m - 1, d));
                        }}
                      />
                      <input type="time" data-testid="event-time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} />
                    </div>
                    <input data-testid="event-rsvp" className={inputCls} placeholder="RSVP URL (optional)" value={form.rsvp_url} onChange={(e) => setForm({ ...form, rsvp_url: e.target.value })} />
                  </div>
                  <DialogFooter>
                    <button data-testid="save-event-btn" onClick={saveEvent} className="px-5 py-2.5 rounded-full bg-white text-black font-semibold hover:bg-purple-500 hover:text-white transition-colors">
                      {editing ? "Update" : "Create"}
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="glass rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50">Title</TableHead>
                    <TableHead className="text-white/50">Date</TableHead>
                    <TableHead className="text-white/50">Location</TableHead>
                    <TableHead className="text-white/50 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id} className="border-white/5" data-testid={`admin-event-${ev.id}`}>
                      <TableCell className="font-medium">{ev.title}</TableCell>
                      <TableCell className="text-white/60">{format(new Date(ev.starts_at), "PPp")}</TableCell>
                      <TableCell className="text-white/60">{ev.location}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`event-menu-${ev.id}`} className="p-2 hover:text-purple-400 transition-colors"><MoreVertical size={16} /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[#0c0c0c] border-white/10 text-white">
                            <DropdownMenuItem onClick={() => openEdit(ev)} className="cursor-pointer"><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => removeEvent(ev.id)} className="cursor-pointer text-red-400"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-white/40 py-8">No events yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* VOLUNTEERS */}
          <TabsContent value="volunteers" className="mt-6">
            <div className="glass rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50">Name</TableHead>
                    <TableHead className="text-white/50">Email</TableHead>
                    <TableHead className="text-white/50">Phone</TableHead>
                    <TableHead className="text-white/50">Interests</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {volunteers.map((v) => (
                    <TableRow key={v.id} className="border-white/5" data-testid={`admin-vol-${v.id}`}>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="text-white/60">{v.email}</TableCell>
                      <TableCell className="text-white/60">{v.phone || "—"}</TableCell>
                      <TableCell className="text-white/60">{(v.interests || []).join(", ") || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {volunteers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-white/40 py-8">No volunteers yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* DONATIONS */}
          <TabsContent value="donations" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display uppercase text-2xl">Donations</h2>
              <button
                data-testid="export-donations-btn"
                onClick={exportDonations}
                disabled={donations.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/20 hover:border-purple-500 hover:text-purple-300 text-sm font-semibold transition-colors disabled:opacity-40"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
            <div className="glass rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50">Donor</TableHead>
                    <TableHead className="text-white/50">Email</TableHead>
                    <TableHead className="text-white/50">Amount</TableHead>
                    <TableHead className="text-white/50">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {donations.map((d) => (
                    <TableRow key={d.id} className="border-white/5" data-testid={`admin-donation-${d.id}`}>
                      <TableCell className="font-medium">{d.donor_name || "Anonymous"}</TableCell>
                      <TableCell className="text-white/60">{d.donor_email || "—"}</TableCell>
                      <TableCell className="text-purple-400 font-semibold">${(d.amount / 100).toFixed(2)} {(d.currency || "cad").toUpperCase()}</TableCell>
                      <TableCell className="text-white/60">{d.updated_at ? format(new Date(d.updated_at), "PPp") : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {donations.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-white/40 py-8">No donations yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* RSVPS */}
          <TabsContent value="rsvps" className="mt-6">
            <div className="glass rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50">Name</TableHead>
                    <TableHead className="text-white/50">Email</TableHead>
                    <TableHead className="text-white/50">Event</TableHead>
                    <TableHead className="text-white/50">RSVP&apos;d</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rsvps.map((r) => (
                    <TableRow key={r.id} className="border-white/5" data-testid={`admin-rsvp-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-white/60">{r.email}</TableCell>
                      <TableCell className="text-white/60">{r.event_title}</TableCell>
                      <TableCell className="text-white/60">{r.created_at ? format(new Date(r.created_at), "PPp") : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {rsvps.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-white/40 py-8">No RSVPs yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* CANDIDATE */}
          <TabsContent value="candidate" className="mt-6">
            {cForm && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="glass rounded-xl p-6 space-y-4">
                  <h3 className="font-display uppercase text-xl">Profile</h3>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Candidate name</label>
                    <input data-testid="cand-name" className={inputCls + " mt-1"} value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Portrait image URL</label>
                    <input data-testid="cand-portrait" className={inputCls + " mt-1"} value={cForm.portrait} onChange={(e) => setCForm({ ...cForm, portrait: e.target.value })} />
                    {cForm.portrait ? <img src={cForm.portrait} alt="preview" className="mt-3 h-40 w-full object-cover object-top rounded-sm border border-white/10" /> : null}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="text-xs uppercase tracking-wider text-white/50">Stat 1</label><input data-testid="cand-stat1" className={inputCls + " mt-1"} value={cForm.stat1} onChange={(e) => setCForm({ ...cForm, stat1: e.target.value })} /></div>
                    <div><label className="text-xs uppercase tracking-wider text-white/50">Stat 2</label><input data-testid="cand-stat2" className={inputCls + " mt-1"} value={cForm.stat2} onChange={(e) => setCForm({ ...cForm, stat2: e.target.value })} /></div>
                    <div><label className="text-xs uppercase tracking-wider text-white/50">Stat 3</label><input data-testid="cand-stat3" className={inputCls + " mt-1"} value={cForm.stat3} onChange={(e) => setCForm({ ...cForm, stat3: e.target.value })} /></div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Fundraising goal (CAD $)</label>
                    <input type="number" min="0" data-testid="cand-goal" className={inputCls + " mt-1"} value={cForm.donation_goal} onChange={(e) => setCForm({ ...cForm, donation_goal: parseInt(e.target.value || 0, 10) })} />
                  </div>
                </div>

                <div className="glass rounded-xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display uppercase text-xl">Bio</h3>
                    <div className="flex gap-1">
                      {["en", "fr", "es", "pt", "it"].map((l) => (
                        <button key={l} data-testid={`bio-lang-${l}`} onClick={() => setBioLang(l)}
                          className={`px-3 py-1.5 rounded-full text-xs uppercase transition-colors ${bioLang === l ? "bg-purple-600 text-white" : "border border-white/15 text-white/60 hover:border-purple-500"}`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Lead ({bioLang.toUpperCase()})</label>
                    <textarea data-testid="cand-lead" rows={3} className={inputCls + " mt-1"} value={(cForm.bios[bioLang] || {}).lead || ""} onChange={setBio("lead")} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/50">Body ({bioLang.toUpperCase()})</label>
                    <textarea data-testid="cand-body" rows={6} className={inputCls + " mt-1"} value={(cForm.bios[bioLang] || {}).body || ""} onChange={setBio("body")} />
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <button data-testid="save-content-btn" onClick={saveContent} disabled={savingContent}
                    className="flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold hover:bg-purple-500 hover:text-white transition-colors disabled:opacity-60">
                    <Save size={16} /> {savingContent ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
